import { c as _c } from "react/compiler-runtime";
import { MouseEventHandler, RefObject, useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { X } from "lucide-react";
import { useTranslations } from "modules/i18n/hooks/useTranslations";

// Note: This is a tsx translation with a few small tweaks for its new environment of:
// rails/app/assets/javascripts/components/promos/DonationDialog/index.jsx
// Please make sure to update both at the same time for future changes
// TODO: Create a component library so can more easily share things between frontend/rails

interface PersonProps {
  avatar: string;
  name: string;
  caption: string;
}
interface DonationDialogProps {
  title: string;
  body: string;
  donateText: string;
  person: PersonProps;
  onClose: Function;
  onClick: MouseEventHandler<HTMLButtonElement>;
  creativeRef: RefObject<HTMLDivElement>;
  modal?: boolean;
}
export const DonationDialog = t0 => {
  "use memo";

  const $ = _c(47);
  const {
    title,
    body,
    donateText,
    person,
    onClose,
    onClick,
    creativeRef,
    modal: t1
  } = t0;
  const modal = t1 === undefined ? true : t1;
  const dialogRef = useRef(null);
  const t = useTranslations("promos");
  let t2;
  let t3;
  if ($[0] !== modal) {
    t2 = () => {
      if (dialogRef.current?.open) {
        return;
      }
      modal ? dialogRef.current?.showModal?.() : dialogRef.current?.show?.();
    };
    t3 = [modal, dialogRef];
    $[0] = modal;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useLayoutEffect(t2, t3);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      dialogRef?.current?.open && dialogRef?.current?.close();
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const handleClose = t4;
  let t5;
  if ($[4] !== onClose) {
    t5 = () => onClose();
    $[4] = onClose;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const onCloseWrapper = t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = clickEvent => {
      if (clickEvent.target === dialogRef.current) {
        handleClose();
      }
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const handleClickOutside = t6;
  let t7;
  let t8;
  if ($[7] !== onCloseWrapper) {
    t7 = () => {
      const currentDialogRef = dialogRef?.current;
      currentDialogRef?.addEventListener("close", onCloseWrapper);
      const requireManualClickOutsideSupport = !("closedBy" in HTMLDialogElement.prototype);
      if (requireManualClickOutsideSupport) {
        currentDialogRef?.addEventListener("click", handleClickOutside);
      }
      return () => {
        currentDialogRef?.removeEventListener("close", onCloseWrapper);
        if (requireManualClickOutsideSupport) {
          currentDialogRef?.removeEventListener("click", handleClickOutside);
        }
      };
    };
    t8 = [dialogRef, onCloseWrapper, handleClickOutside];
    $[7] = onCloseWrapper;
    $[8] = t7;
    $[9] = t8;
  } else {
    t7 = $[8];
    t8 = $[9];
  }
  useEffect(t7, t8);
  const t9 = modal ? "any" : "none";
  let t10;
  if ($[10] !== t9) {
    t10 = {
      closedby: t9
    };
    $[10] = t9;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const closedByType = t10 as const;
  let t11;
  if ($[12] !== t) {
    t11 = t("button.close");
    $[12] = t;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <X stroke="currentColor" className="size-6" />;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== t11) {
    t13 = <button className="absolute top-4 right-4 flex cursor-pointer items-center justify-center rounded-full border-none bg-transparent p-3 text-white hover:bg-black/10" type="button" onClick={handleClose} aria-label={t11}>{t12}</button>;
    $[15] = t11;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== person.avatar) {
    t14 = <div className="-mt-14 flex justify-center"><img className="size-16 rounded-full border border-solid border-gray-200" src={person.avatar} alt="" /></div>;
    $[17] = person.avatar;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== title) {
    t15 = <h2 id="dialog-header" className="font-sans-heading mt-9 text-xl font-semibold break-words">{title}</h2>;
    $[19] = title;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] !== body) {
    t16 = <p className="mt-5 text-base leading-snug break-words">{body}</p>;
    $[21] = body;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <hr className="mx-auto my-4 max-w-5 border-white" />;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  let t18;
  if ($[24] !== person.name) {
    t18 = <div className="text-base">{person.name}</div>;
    $[24] = person.name;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  let t19;
  if ($[26] !== person.caption) {
    t19 = <div className="text-xs">{person.caption}</div>;
    $[26] = person.caption;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  let t20;
  if ($[28] !== t18 || $[29] !== t19) {
    t20 = <div className="flex items-center justify-center"><div className="text-center">{t18}{t19}</div></div>;
    $[28] = t18;
    $[29] = t19;
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  let t21;
  if ($[31] !== t15 || $[32] !== t16 || $[33] !== t20) {
    t21 = <div className="mt-4 text-center font-sans">{t15}{t16}{t17}{t20}</div>;
    $[31] = t15;
    $[32] = t16;
    $[33] = t20;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  let t22;
  if ($[35] !== donateText || $[36] !== onClick) {
    t22 = <div className="my-6 flex justify-center"><button id="goToDonationsButton" className="cursor-pointer rounded-sm border-none bg-white px-12 py-3 text-base font-semibold break-words text-gray-900 transition-colors duration-300 hover:bg-[#f1f1f2]" type="button" onClick={onClick} data-testid="actionButton">{donateText}</button></div>;
    $[35] = donateText;
    $[36] = onClick;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  let t23;
  if ($[38] !== creativeRef || $[39] !== t13 || $[40] !== t14 || $[41] !== t21 || $[42] !== t22) {
    t23 = <section ref={creativeRef} className="rounded-md bg-red-500 p-6 text-white shadow-lg sm:max-w-lg">{t13}{t14}{t21}{t22}</section>;
    $[38] = creativeRef;
    $[39] = t13;
    $[40] = t14;
    $[41] = t21;
    $[42] = t22;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  let t24;
  if ($[44] !== closedByType || $[45] !== t23) {
    t24 = <dialog ref={dialogRef} aria-labelledby="dialog-header" className="m-auto overflow-visible rounded-md border-none p-0 backdrop:fixed backdrop:inset-0 backdrop:h-full backdrop:bg-black/50 sm:items-start" {...closedByType}>{t23}</dialog>;
    $[44] = closedByType;
    $[45] = t23;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  return t24;
};